import type { CompoundClause, TerminalClause } from '@atlaskit/jql-ast/ast/clause';
import type { Operand } from '@atlaskit/jql-ast/ast/operand';
import type { OperatorValue } from '@atlaskit/jql-ast/ast/operator';
import type { OrderByDirectionValue } from '@atlaskit/jql-ast/ast/order-by';
import type { Predicate, PredicateOperatorValue } from '@atlaskit/jql-ast/ast/predicate';
import { CLAUSE_TYPE_COMPOUND, CLAUSE_TYPE_TERMINAL } from '@atlaskit/jql-ast/clause';
import { COMPOUND_OPERATOR_AND, COMPOUND_OPERATOR_OR } from '@atlaskit/jql-ast/compound';
import { JastBuilder } from '@atlaskit/jql-ast/jast-builder';
import { walkAST } from '@atlaskit/jql-ast/jast-walker';
import {
	OPERAND_TYPE_FUNCTION,
	OPERAND_TYPE_KEYWORD,
	OPERAND_TYPE_LIST,
	OPERAND_TYPE_VALUE,
} from '@atlaskit/jql-ast/operand';
import { OPERATOR_EQUALS, OPERATOR_IN } from '@atlaskit/jql-ast/operator';
import { ORDER_BY_DIRECTION_ASC } from '@atlaskit/jql-ast/order-by';

export type ParsedJqlPredicate = {
	operator: PredicateOperatorValue;
	value: string[];
};

export type ParsedJqlClause = {
	field: string;
	operator: OperatorValue;
	value: string[];
	// Only set when the clause's value can't be resolved as normal operand values (currently only
	// CHANGED clauses, whose operand is always undefined) — carries the raw JQL substring instead
	// of dropping the clause entirely.
	rawText?: string;
	// BY user / DURING window scoping carried by WAS/WAS IN/WAS NOT/WAS NOT IN clauses.
	predicates?: ParsedJqlPredicate[];
};

export type ParsedJqlSort = {
	field: string;
	direction: OrderByDirectionValue;
};

export type ParsedJql = {
	clauses: ParsedJqlClause[];
	// True when the JQL contains structure (NOT, or nested AND/OR grouping) that can't be
	// flattened into a flat list of filter chips without misrepresenting the query.
	isComplex: boolean;
	sorts: ParsedJqlSort[];
};

// Keyword sentinel that the builder emits for the JQL `EMPTY` keyword operand.
const EMPTY_KEYWORD_VALUE = 'EMPTY';

const singleOperandToValue = (operand: Operand): string | undefined => {
	switch (operand.operandType) {
		case OPERAND_TYPE_VALUE:
			return operand.value;
		case OPERAND_TYPE_KEYWORD:
			return EMPTY_KEYWORD_VALUE;
		case OPERAND_TYPE_FUNCTION: {
			const args = operand.arguments.map((argument) => argument.value).join(', ');
			return `${operand.function.value}(${args})`;
		}
		default:
			// Nested list operands are not valid JQL, so there is nothing to extract here.
			return undefined;
	}
};

const operandToValues = (operand: Operand): string[] => {
	if (operand.operandType !== OPERAND_TYPE_LIST) {
		const value = singleOperandToValue(operand);
		return value === undefined ? [] : [value];
	}

	const values: string[] = [];
	for (const value of operand.values) {
		const resolved = singleOperandToValue(value);
		if (resolved !== undefined) {
			values.push(resolved);
		}
	}
	return values;
};

const FIELD_ALIASES: Record<string, string> = {
	due: 'duedate',
	issuetype: 'type',
	key: 'issuekey',
};

const getCanonicalFieldKey = (field: string): string => {
	const normalizedField = field.toLowerCase();
	return FIELD_ALIASES[normalizedField] ?? normalizedField;
};

const isPositiveMembershipOperator = (operator: OperatorValue | undefined): boolean =>
	operator === OPERATOR_EQUALS || operator === OPERATOR_IN;

const isStaticOperand = (operand: Operand | void): boolean => {
	if (!operand) {
		return false;
	}
	if (operand.operandType === OPERAND_TYPE_LIST) {
		return operand.values.every((value) => value.operandType !== OPERAND_TYPE_FUNCTION);
	}
	return operand.operandType !== OPERAND_TYPE_FUNCTION;
};

const isStaticPositiveMembershipClause = (terminalClause: TerminalClause): boolean =>
	isPositiveMembershipOperator(terminalClause.operator?.value) &&
	isStaticOperand(terminalClause.operand);

const getSafeOrField = (
	compoundClause: CompoundClause,
	requiresStaticMembership = false,
): string | undefined => {
	if (compoundClause.operator.value !== COMPOUND_OPERATOR_OR) {
		return undefined;
	}

	let field: string | undefined;
	for (const clause of compoundClause.clauses) {
		if (clause.clauseType === CLAUSE_TYPE_TERMINAL) {
			const terminalClause = clause as TerminalClause;
			if (
				!isPositiveMembershipOperator(terminalClause.operator?.value) ||
				(requiresStaticMembership && !isStaticPositiveMembershipClause(terminalClause))
			) {
				return undefined;
			}
			const terminalField = getCanonicalFieldKey(terminalClause.field.value);
			if (field && field !== terminalField) {
				return undefined;
			}
			field = terminalField;
			continue;
		}

		if (clause.clauseType !== CLAUSE_TYPE_COMPOUND) {
			return undefined;
		}
		const nestedField = getSafeOrField(clause as CompoundClause, requiresStaticMembership);
		if (!nestedField || (field && field !== nestedField)) {
			return undefined;
		}
		field = nestedField;
	}

	return field;
};

const getSafeAndFields = (
	compoundClause: CompoundClause,
	requiresStaticMembership = false,
): string[] | undefined => {
	if (compoundClause.operator.value !== COMPOUND_OPERATOR_AND) {
		return undefined;
	}

	const requiresStaticMembershipForGroup =
		requiresStaticMembership ||
		compoundClause.clauses.some(
			(clause) =>
				clause.clauseType === CLAUSE_TYPE_COMPOUND &&
				(clause as CompoundClause).operator.value === COMPOUND_OPERATOR_OR,
		);
	const fields: string[] = [];
	for (const clause of compoundClause.clauses) {
		if (clause.clauseType === CLAUSE_TYPE_TERMINAL) {
			const terminalClause = clause as TerminalClause;
			if (requiresStaticMembershipForGroup && !isStaticPositiveMembershipClause(terminalClause)) {
				return undefined;
			}
			fields.push(getCanonicalFieldKey(terminalClause.field.value));
			continue;
		}
		if (clause.clauseType !== CLAUSE_TYPE_COMPOUND) {
			return undefined;
		}

		const nestedClause = clause as CompoundClause;
		const nestedFields =
			nestedClause.operator.value === COMPOUND_OPERATOR_AND
				? getSafeAndFields(nestedClause, requiresStaticMembershipForGroup)
				: getSafeOrField(nestedClause, true);
		if (!nestedFields) {
			return undefined;
		}
		fields.push(...(Array.isArray(nestedFields) ? nestedFields : [nestedFields]));
	}

	return new Set(fields).size === fields.length ? fields : undefined;
};

export const parseViewJql = (jql: string): ParsedJql => {
	const trimmedJql = jql.trim();
	if (trimmedJql === '') {
		return { clauses: [], isComplex: false, sorts: [] };
	}

	const jast = new JastBuilder().build(trimmedJql);
	const { query } = jast;
	if (!query || jast.errors.length > 0) {
		return { clauses: [], isComplex: true, sorts: [] };
	}

	const clauses: ParsedJqlClause[] = [];
	let isComplex = false;
	walkAST(
		{
			enterTerminalClause: (clause) => {
				if (clause.operator && clause.operand) {
					const predicates: ParsedJqlPredicate[] = clause.predicates.map(
						(predicate: Predicate) => ({
							operator: predicate.operator.value,
							value: predicate.operand ? operandToValues(predicate.operand) : [],
						}),
					);
					clauses.push({
						field: clause.field.value,
						operator: clause.operator.value,
						value: operandToValues(clause.operand),
						...(predicates.length > 0 ? { predicates } : {}),
					});
				} else if (clause.operator) {
					// CHANGED clauses always have an undefined operand (@atlaskit/jql-ast never resolves
					// one for them), so they'd otherwise be silently dropped. Fall back to the raw JQL
					// substring rather than losing the clause entirely.
					const [start, end] = clause.position ?? [0, -1];
					clauses.push({
						field: clause.field.value,
						operator: clause.operator.value,
						value: [],
						rawText: trimmedJql.slice(start, end + 1),
					});
				}
			},
			// A NOT-wrapped clause can never be represented as flat chips.
			enterNotClause: () => {
				isComplex = true;
			},
			enterCompoundClause: (compoundClause) => {
				const isSafe =
					compoundClause.operator.value === COMPOUND_OPERATOR_AND
						? Boolean(getSafeAndFields(compoundClause))
						: Boolean(getSafeOrField(compoundClause));
				if (!isSafe) {
					isComplex = true;
				}
			},
		},
		jast,
	);

	const sorts: ParsedJqlSort[] = (query.orderBy?.fields ?? []).map((sort) => ({
		field: sort.field.value,
		// An omitted direction (e.g. "ORDER BY created") means JQL's default of ascending.
		direction: sort.direction?.value ?? ORDER_BY_DIRECTION_ASC,
	}));

	return { clauses, isComplex, sorts };
};
